import { FormControl, TextareaAutosize, TextField } from "@material-ui/core";
import React from "react";

import { ScheduledJobBase, ScheduledJobTarget, JobParameter } from "../../../../dtos/scheduledJob";
import { ScheduledJobType } from "../../../../enums/ScheduledJobType";
import { ScheduledJobScriptType } from "../../../../enums/ScheduledJobScriptType";
import { FormSection } from "../../../shared/formSection";
import { FormField } from "../../../shared/formField";

import { ScheduledJobScriptTypeSelector } from "./ScriptTypeSelector";
import { ScheduledJobTypeSelector } from "./TypeSelector";
import { ScheduledJobTargetsFormSection } from "./TargetsFormSection";
import { getTargetLabel } from "./utils";

interface Props {
    jobBase: ScheduledJobBase;
    setJobBase: (jobBase: ScheduledJobBase) => void;
    availableJobTypes: ScheduledJobType[];
    initialJobBase?: ScheduledJobBase;
}

export function ScheduledJobForm({
    jobBase,
    setJobBase,
    availableJobTypes,
    initialJobBase,
}: Props) {

    const handleTypeChange = (type: ScheduledJobType) => {
        setJobBase({ ...jobBase, type, data: getScheduledJobData(type) });
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setJobBase({ ...jobBase, name: e.target.value });
    };

    const handleScriptTypeChange = (type: ScheduledJobScriptType, index: number) => {
        if (!jobBase.data) return;
        const newData = [...jobBase.data];
        newData[index] = { ...newData[index], type };
        setJobBase({ ...jobBase, data: newData });
    };

    const handleScriptChange = (value: string, index: number) => {
        if (!jobBase.data) return;
        const newData = [...jobBase.data];
        newData[index] = { ...newData[index], value };
        setJobBase({ ...jobBase, data: newData });
    };


    const handleAddTarget = (target: ScheduledJobTarget) => {
        setJobBase({ ...jobBase, targets: [...jobBase.targets, target] })
    };

    const handleRemoveTargetByIndex = (indexToRemove: number) => {
        const newTargets = [...jobBase.targets];
        newTargets.splice(indexToRemove, 1)
        setJobBase({ ...jobBase, targets: newTargets })
    }

    const jobTargetIndexLabelMap = jobBase.targets
        .map((it, i) => ({ target: it, index: i, label: getTargetLabel(it) }));

    return (
        <>
            <FormSection headerText="Job Settings">
                <FormField label="Job Name">
                    <FormControl style={{ minWidth: "300px" }}>
                        <TextField
                            value={jobBase.name}
                            onChange={handleNameChange}
                            variant="outlined"
                            InputProps={{
                                inputProps: { style: { padding: "10px" } },
                            }}
                        />
                    </FormControl>
                </FormField>

                <FormField label="Job Type">
                    <FormControl style={{ minWidth: "300px" }}>
                        <ScheduledJobTypeSelector
                            selectedType={jobBase.type}
                            onTypeChange={handleTypeChange}
                            availableTypes={availableJobTypes}
                        />
                    </FormControl>
                </FormField>

                {initialJobBase && jobBase.type !== initialJobBase.type && (
                    <p>Changes to the job type will only take effect tomorrow if today’s job has already been completed</p>
                )}

                {jobBase.type === ScheduledJobType.CustomScript && jobBase.data && (
                    <>
                        {jobBase.data.map((script, index) => (
                            <React.Fragment key={index}>
                                <FormField label={`Script Type`}>
                                    <FormControl style={{ minWidth: "300px" }}>
                                        <ScheduledJobScriptTypeSelector
                                            selectedScriptType={script.type}
                                            onScriptTypeChange={(type) => handleScriptTypeChange(type, index)}
                                        />
                                    </FormControl>
                                </FormField>

                                <FormField label={`Script`}>
                                    <FormControl style={{ minWidth: "300px" }}>
                                        <TextareaAutosize
                                            rows={3}
                                            placeholder={getPlaceholderForScriptType(script.type)}
                                            value={script.value}
                                            onChange={(e) => handleScriptChange(e.target.value, index)}
                                        />
                                    </FormControl>
                                </FormField>
                            </React.Fragment>
                        ))}
                    </>
                )}
            </FormSection>

            <ScheduledJobTargetsFormSection
                targetIndexLabelMap={jobTargetIndexLabelMap}
                isExclusion={false}
                onAddTarget={handleAddTarget}
                onRemoveTargetByIndex={handleRemoveTargetByIndex}
            />

            <ScheduledJobTargetsFormSection
                targetIndexLabelMap={jobTargetIndexLabelMap}
                isExclusion={true}
                onAddTarget={handleAddTarget}
                onRemoveTargetByIndex={handleRemoveTargetByIndex}
            />
        </>
    );
}

function getScheduledJobData(type: ScheduledJobType): JobParameter[] | undefined {
    if (type !== ScheduledJobType.CustomScript) return undefined;

    return [{
        type: ScheduledJobScriptType.Sh,
        value: "",
        name: null //Igor has requested name of the steps,there probably gonna be more then one parameter for one job in the future. So i think its gonna be useful,but no implementation at the moment.
    }];
}

function getPlaceholderForScriptType(type: ScheduledJobScriptType): string {
    switch (type) {
        case (ScheduledJobScriptType.Sh):
            return 'echo "Custom script is running!"';
        default: return "";
    }
}
